// Shared panel-grid dialog behaviour (layout persistence, tabsheets, field picker, window resize)
// consumed by the package, mapping-profile and vehicle edit dialogs.
import { useLocalStorage } from '@vueuse/core';
import { computed, ref, watch, type Ref } from 'vue';
import {
  createPanelGridLayout,
  migratePanelGridLayout,
  movePanelGridGroupToNewTabsheet,
  movePanelGridGroupToTabsheet,
  reorderPanelGridTabsheets,
  type PanelGridConfig,
  type PanelGridFieldSettings,
  type PanelGridGroup,
  type PanelGridLayoutState,
  type PanelGridTabsheet,
  type StoredPanelGridLayoutState,
} from './panelGridLayout';

export interface UsePanelGridDialogOptions {
  config: PanelGridConfig;
  layoutStorageKey: string;
  editModeStorageKey: string;
  // Dialog visibility, used to stop an in-progress resize and re-clamp position on open.
  visible: Ref<boolean>;
}

export function usePanelGridDialog<TPanelId extends string>(options: UsePanelGridDialogOptions) {
  const { config, visible } = options;

  const storedLayoutState = useLocalStorage<StoredPanelGridLayoutState>(options.layoutStorageKey, () =>
    createPanelGridLayout(config),
  );
  const layoutEditMode = useLocalStorage<boolean>(options.editModeStorageKey, true);
  const gridLayout = ref<PanelGridLayoutState>(migratePanelGridLayout(config, storedLayoutState.value));

  const activeTabsheet = computed(
    () =>
      gridLayout.value.tabsheets.find((sheet) => sheet.id === gridLayout.value.activeTabsheetId) ??
      gridLayout.value.tabsheets[0],
  );

  const gridGroups = computed<PanelGridGroup[]>({
    get: () => activeTabsheet.value?.groups ?? [],
    set: (value) => {
      const activeId = gridLayout.value.activeTabsheetId;
      gridLayout.value = {
        ...gridLayout.value,
        tabsheets: gridLayout.value.tabsheets.map((sheet) =>
          sheet.id === activeId ? { ...sheet, groups: value } : sheet,
        ),
      };
    },
  });

  const editingSheetId = ref<string | null>(null);
  const editingSheetName = ref('');
  const draggedSheetId = ref<string | null>(null);
  const hoverSheetId = ref<string | null>(null);

  function selectTabsheet(id: string) {
    gridLayout.value = { ...gridLayout.value, activeTabsheetId: id };
  }

  // Restore the default tabsheet/panel/field arrangement while keeping the current window geometry.
  function resetLayout() {
    gridLayout.value = { ...createPanelGridLayout(config), window: { ...gridLayout.value.window } };
  }

  function moveGroupToNewSheet(groupId: string) {
    const { tabsheets, newTabsheetId } = movePanelGridGroupToNewTabsheet(gridLayout.value.tabsheets, groupId);
    if (!newTabsheetId) {
      return;
    }
    gridLayout.value = { ...gridLayout.value, tabsheets, activeTabsheetId: newTabsheetId };
  }

  function moveGroupToSheet(groupId: string, sheetId: string) {
    const tabsheets = movePanelGridGroupToTabsheet(gridLayout.value.tabsheets, groupId, sheetId);
    if (tabsheets === gridLayout.value.tabsheets) {
      return;
    }
    const activeExists = tabsheets.some((sheet) => sheet.id === gridLayout.value.activeTabsheetId);
    gridLayout.value = {
      ...gridLayout.value,
      tabsheets,
      activeTabsheetId: activeExists
        ? gridLayout.value.activeTabsheetId
        : (tabsheets[0]?.id ?? gridLayout.value.activeTabsheetId),
    };
  }

  function startRenameTabsheet(sheet: PanelGridTabsheet) {
    editingSheetId.value = sheet.id;
    editingSheetName.value = sheet.name;
  }

  function commitRenameTabsheet() {
    const id = editingSheetId.value;
    editingSheetId.value = null;
    const sheetName = editingSheetName.value.trim();
    if (!id || !sheetName) {
      return;
    }
    gridLayout.value = {
      ...gridLayout.value,
      tabsheets: gridLayout.value.tabsheets.map((sheet) => (sheet.id === id ? { ...sheet, name: sheetName } : sheet)),
    };
  }

  function onTabsheetDrop(targetId: string) {
    const sourceId = draggedSheetId.value;
    draggedSheetId.value = null;
    if (sourceId && sourceId !== targetId) {
      gridLayout.value = {
        ...gridLayout.value,
        tabsheets: reorderPanelGridTabsheets(gridLayout.value.tabsheets, sourceId, targetId),
      };
    }
  }

  function panelFieldSettings(panelId: TPanelId): PanelGridFieldSettings {
    return gridLayout.value.fieldSettings[panelId] ?? { labelWidth: config.label.default, hidden: [] };
  }

  function isFieldVisible(panelId: TPanelId, key: string): boolean {
    return !panelFieldSettings(panelId).hidden.includes(key);
  }

  function setFieldVisible(panelId: TPanelId, key: string, isVisible: boolean) {
    const settings = panelFieldSettings(panelId);
    const hidden = new Set(settings.hidden);
    if (isVisible) {
      hidden.delete(key);
    } else {
      hidden.add(key);
    }
    gridLayout.value = {
      ...gridLayout.value,
      fieldSettings: { ...gridLayout.value.fieldSettings, [panelId]: { ...settings, hidden: [...hidden] } },
    };
  }

  function setLabelWidth(panelId: TPanelId, labelWidth: number) {
    const settings = panelFieldSettings(panelId);
    gridLayout.value = {
      ...gridLayout.value,
      fieldSettings: { ...gridLayout.value.fieldSettings, [panelId]: { ...settings, labelWidth } },
    };
  }

  const activePickerPanel = ref<TPanelId>(config.panelOrder[0] as TPanelId);

  watch(
    gridLayout,
    (state) => {
      storedLayoutState.value = {
        schemaVersion: state.schemaVersion,
        window: { ...state.window },
        tabsheets: state.tabsheets,
        activeTabsheetId: state.activeTabsheetId,
        fieldSettings: state.fieldSettings,
      };
    },
    { deep: true },
  );

  const dialogStyle = computed(() => {
    const style: Record<string, string> = {
      width: `${gridLayout.value.window.width}px`,
    };

    if (gridLayout.value.window.left !== null && gridLayout.value.window.top !== null) {
      style.left = `${gridLayout.value.window.left}px`;
      style.top = `${gridLayout.value.window.top}px`;
      style.margin = '0';
    }

    return style;
  });

  const dialogContentStyle = computed(() => ({
    height: `${gridLayout.value.window.height}px`,
    overflow: 'hidden',
  }));

  interface DialogResizeState {
    pointerStartX: number;
    pointerStartY: number;
    widthStart: number;
    heightStart: number;
  }

  const dialogResizeState = ref<DialogResizeState | null>(null);

  function clampNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  function clampDialogPosition() {
    if (typeof window === 'undefined') {
      return;
    }

    if (gridLayout.value.window.left === null || gridLayout.value.window.top === null) {
      return;
    }

    const maxLeft = Math.max(0, window.innerWidth - gridLayout.value.window.width);
    const maxTop = Math.max(0, window.innerHeight - 120);

    gridLayout.value.window.left = clampNumber(gridLayout.value.window.left, 0, maxLeft);
    gridLayout.value.window.top = clampNumber(gridLayout.value.window.top, 0, maxTop);
  }

  function startResize(event: MouseEvent) {
    dialogResizeState.value = {
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      widthStart: gridLayout.value.window.width,
      heightStart: gridLayout.value.window.height,
    };

    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', stopResize);
  }

  function stopResize() {
    dialogResizeState.value = null;
    window.removeEventListener('mousemove', onPointerMove);
    window.removeEventListener('mouseup', stopResize);
  }

  function onPointerMove(event: MouseEvent) {
    if (!dialogResizeState.value) {
      return;
    }

    const deltaX = event.clientX - dialogResizeState.value.pointerStartX;
    const deltaY = event.clientY - dialogResizeState.value.pointerStartY;

    const nextWidth = dialogResizeState.value.widthStart + deltaX;
    const nextHeight = dialogResizeState.value.heightStart + deltaY;

    gridLayout.value.window.width = clampNumber(
      nextWidth,
      config.window.minWidth,
      Math.max(config.window.minWidth, window.innerWidth - 40),
    );
    gridLayout.value.window.height = clampNumber(
      nextHeight,
      config.window.minHeight,
      Math.max(config.window.minHeight, window.innerHeight - 160),
    );
    clampDialogPosition();
  }

  watch(visible, (isVisible) => {
    if (!isVisible) {
      stopResize();
      return;
    }

    // Layout customization always starts disabled when the dialog opens.
    layoutEditMode.value = false;
    clampDialogPosition();
  });

  return {
    layoutEditMode,
    gridLayout,
    activeTabsheet,
    gridGroups,
    editingSheetId,
    editingSheetName,
    draggedSheetId,
    hoverSheetId,
    selectTabsheet,
    resetLayout,
    moveGroupToNewSheet,
    moveGroupToSheet,
    startRenameTabsheet,
    commitRenameTabsheet,
    onTabsheetDrop,
    panelFieldSettings,
    isFieldVisible,
    setFieldVisible,
    setLabelWidth,
    activePickerPanel,
    dialogStyle,
    dialogContentStyle,
    startResize,
  };
}
